import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { ssStyles, STATUS_META } from '../components/source-status/styles';
import { SourceCard } from '../components/source-status/SourceCard';
import { StatusEditor } from '../components/source-status/StatusEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/handbook/sources`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const TABS = ['unverified', 'current', 'replaced', 'all'];
const errText = (e) => e?.response?.data?.detail || 'Something went wrong. Please try again.';

const useSources = () => {
  const [data, setData] = useState({ sources: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await axios.get(`${API}?status=all`, auth())).data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { ...data, loading, load };
};

const Tabs = ({ tab, setTab, counts }) => (
  <div className="ss-tabs" data-testid="source-status-tabs">
    {TABS.map((t) => (
      <button key={t} className={`ss-tab ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}
        style={{ '--c': STATUS_META[t]?.color || '#3b82f6' }} data-testid={`source-tab-${t}`}>
        <div className="ss-tab-n" data-testid={`source-count-${t}`}>{t === 'all' ? counts.total ?? 0 : counts[t] ?? 0}</div>
        <div className="ss-tab-l">{STATUS_META[t]?.label || 'All'}</div>
      </button>
    ))}
  </div>
);

export default function SourceStatusPage() {
  const navigate = useNavigate();
  const { sources, counts, loading, load } = useSources();
  const [tab, setTab] = useState('unverified');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const shown = useMemo(() => sources.filter((s) => (tab === 'all' || s.source_status === tab)
    && s.title.toLowerCase().includes(q.trim().toLowerCase())), [sources, tab, q]);
  const allOn = shown.length > 0 && shown.every((s) => selected.includes(s.id));
  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => setSelected(allOn ? [] : shown.map((s) => s.id));

  const save = async (ids, body) => {
    setBusy(true); setMsg(null);
    try {
      const res = ids.length === 1
        ? await axios.put(`${API}/${ids[0]}`, body, auth())
        : await axios.post(`${API}/bulk`, { ...body, book_ids: ids }, auth());
      const extra = res.data.also_marked_current ? ` "${res.data.also_marked_current}" was marked current.` : '';
      setMsg({ ok: true, text: `Marked ${ids.length} handbook${ids.length > 1 ? 's' : ''} as ${STATUS_META[body.status].label.toLowerCase()}.${extra}` });
      setEditing(null); setSelected([]);
      await load();
    } catch (e) {
      setMsg({ ok: false, text: errText(e) });
    } finally { setBusy(false); }
  };

  return (
    <div className="ss-page" data-testid="source-status-page">
      <style>{ssStyles}</style>
      <header className="ss-head">
        <div>
          <p className="ss-kicker">Ask Stewie · Sources</p>
          <h1 className="ss-title">Source Status</h1>
          <p className="ss-sub">Mark each handbook Stewie quotes as current or replaced. Unverified handbooks show
            "unverified" on every citation, and replaced ones are quoted only as an older version.</p>
        </div>
        <div className="ss-actions">
          <button className="ss-ghost" onClick={() => navigate('/admin/document-governance')} data-testid="source-status-back">
            <ArrowLeft size={14} /> Governance
          </button>
          <button className="ss-ghost" onClick={load} disabled={loading} data-testid="source-status-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      <Tabs tab={tab} setTab={(t) => { setTab(t); setSelected([]); }} counts={counts} />
      {msg && <div className={`ss-msg ${msg.ok ? 'ok' : 'err'}`} data-testid="source-status-message">{msg.text}</div>}

      <div className="ss-tools">
        <input className="ss-search" placeholder="Search handbooks…" value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="source-status-search" />
        <label className="ss-check">
          <input type="checkbox" checked={allOn} onChange={toggleAll} data-testid="source-select-all" /> Select all shown
        </label>
      </div>

      {selected.length > 0 && (
        <div className="ss-bulk" data-testid="source-bulk-bar">
          <div className="ss-bulk-top">
            <strong data-testid="source-bulk-count">{selected.length} selected</strong>
            <button className="ss-ghost" onClick={() => setSelected([])} data-testid="source-bulk-clear">Clear</button>
          </div>
          <StatusEditor key={selected.join()} choices={sources} excludeIds={selected} busy={busy}
            onSave={(b) => save(selected, b)} testPrefix="source-bulk" />
        </div>
      )}

      <div className="ss-list" data-testid="source-status-list">
        {loading && !sources.length && <div className="ss-empty">Loading handbooks…</div>}
        {!loading && !shown.length && <div className="ss-empty" data-testid="source-status-empty">No handbooks here.</div>}
        {shown.map((s, i) => (
          <SourceCard key={s.id} index={i} source={s} choices={sources} busy={busy}
            selected={selected.includes(s.id)} onToggle={() => toggle(s.id)}
            editing={editing === s.id} onEdit={() => setEditing(editing === s.id ? null : s.id)}
            onSave={(b) => save([s.id], b)} />
        ))}
      </div>
    </div>
  );
}
